import moment from 'moment';
import numeral from 'numeral';

import { Aggregation, ChangeIndicatorClass, CompareFunction } from 'webapp/enums/enums';
import { switchDotsAndCommas } from 'webapp/utils/common';

import type DsDataPointOther from 'webapp/models-custom/ds-data/point/other';

window.numeral = numeral; // expose it for global use

const simpleFormatCharacters = '-+()[].,%0#�◊|';

function isNumber(n: string | number | null | undefined): n is string | number {
	// don't use parsed value for isFinite, but original e.g. '2022-04' which will be false
	// (Number(n) is what the global isFinite converts with)
	return !isNaN(parseFloat(String(n))) && isFinite(Number(n));
}

function isDurationFormat(format: string): boolean {
	const durationRegex = new RegExp(/^P+([DdHhMmSs:[\]]*)$/);

	return durationRegex.test(format);
}

function getRelativeTime(timestamp: number): string {
	let text = moment.unix(timestamp).fromNow();

	// transform some cases to: just now
	if (['a minute ago', 'a few seconds ago', 'in a minute', 'in a few seconds'].includes(text)) {
		text = 'just now';
	}

	return text;
}

function getIntervalTime(
	amount: number,
	units: moment.unitOfTime.Base,
	leaveArticles?: boolean,
): string {
	let duration = moment.duration(amount, units).humanize();

	if (!leaveArticles) {
		if (duration === 'an hour') {
			duration = 'hour';
		} else if (duration === 'a minute') {
			duration = 'minute';
		} else if (duration === 'a day') {
			duration = 'day';
		} else if (duration === 'a week') {
			duration = 'week';
		} else if (duration === 'a month') {
			duration = 'month';
		}
	}

	return duration;
}

function getCurrentFormattedTime(tz = 'Etc/UTC', format = 'MMM D, YYYY'): string {
	return moment.tz(tz).format(format);
}

function getCurrentUnixTimestamp(): number {
	return moment().unix();
}

function getFormattedTime(timestamp: number, tz = 'Etc/UTC', format = 'MMM D, YYYY'): string {
	return moment.unix(timestamp).tz(tz).format(format);
}

function getUnixTimestamp(date: string, tz = 'Etc/UTC', format = 'MMM D, YYYY'): number {
	return moment.tz(date, format, tz).unix();
}

function getFormattedPrice(priceInCents: number, decimals?: number, skipCurrency = false): string {
	let formatted = '';

	if (priceInCents < 0) {
		formatted = '-';
		priceInCents = Math.abs(priceInCents);
	}

	const fractionDigits = decimals ?? (priceInCents % 100 === 0 ? 0 : 2);
	const opts = {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	};
	formatted += (skipCurrency ? '' : '$') + (priceInCents / 100).toLocaleString('en-US', opts);

	return formatted;
}

function getFormattedMonthlyPrice(
	priceInCents: number,
	decimals: number,
	monthsInCycle: number,
): string {
	return getFormattedPrice(priceInCents / monthsInCycle, decimals);
}

function getFormattedPeriod(
	tsStart: number,
	tsEnd: number,
	timezone: string | null,
	format = 'MMM D, YYYY',
	formatWithoutYear = 'MMM D',
): string {
	let startMoment = moment.unix(tsStart),
		endMoment = moment.unix(tsEnd);

	if (timezone) {
		startMoment = startMoment.tz(timezone);
		endMoment = endMoment.tz(timezone);
	}

	const yearFormat = 'YYYY',
		currentYear = moment.tz(timezone || 'UTC').format(yearFormat),
		startYear = startMoment.format(yearFormat),
		endYear = endMoment.format(yearFormat);

	const formatToUse =
		startYear === currentYear && startYear === endYear ? formatWithoutYear : format;

	return `${startMoment.format(formatToUse)} - ${endMoment.format(formatToUse)}`;
}

function getDurationString(number: number, format: string): string {
	// https://github.com/databox/core/blob/develop/src/Z/Util/Number.php#L275
	const values: string[] = [], // seconds
		secondsInAMinute = 60,
		secondsInAnHour = 60 * secondsInAMinute,
		secondsInADay = 24 * secondsInAnHour,
		lowerCaseFormat = format.toLowerCase();
	let inputSeconds = Math.round(number),
		formats: { format: string; value: number }[] = [],
		suffixes: { from: string; to: string }[] = [],
		days = 0,
		hours = 0,
		minutes = 0,
		seconds = 0;

	if (lowerCaseFormat.includes('dd')) {
		days = Math.floor(inputSeconds / secondsInADay);
		inputSeconds -= days * secondsInADay;
	}

	if (lowerCaseFormat.includes('hh')) {
		hours = Math.floor(inputSeconds / secondsInAnHour);
		inputSeconds -= hours * secondsInAnHour;
	}
	if (lowerCaseFormat.includes('mm')) {
		minutes = Math.floor(inputSeconds / secondsInAMinute);
		inputSeconds -= minutes * secondsInAMinute;
	}
	if (lowerCaseFormat.includes('ss')) {
		seconds = inputSeconds;
	}

	formats = [
		{
			format: 'dd',
			value: days,
		},
		{
			format: 'hh',
			value: hours,
		},
		{
			format: 'mm',
			value: minutes,
		},
		{
			format: 'ss',
			value: seconds,
		},
	];

	suffixes = [
		{
			from: 'dd',
			to: 'd',
		},
		{
			from: 'hh',
			to: 'h',
		},
		{
			from: 'mm',
			to: 'm',
		},
		{
			from: 'ss',
			to: 's',
		},
	];

	formats.forEach((item) => {
		const idx = lowerCaseFormat.indexOf(item.format);
		if (idx > -1) {
			if (lowerCaseFormat[idx - 1] !== '[' || item.value > 0) {
				const suffix = suffixes.find((s) => s.from === item.format)?.to;
				values.push(suffix ? item.value + suffix : String(item.value));
			}
		}
	});

	if (values.length === 0) {
		return String(inputSeconds);
	}

	return values.join(' ');
}

function splitFormat(format: string): { prefix: string; core: string; postfix: string } {
	let prefix = '',
		core = '',
		postfix = '',
		pre = true,
		post = false;

	format.split('').forEach((char, idx) => {
		const isLegitFormatSymbol = simpleFormatCharacters.includes(char);

		if (!idx && isLegitFormatSymbol) {
			// no prefix at all
			pre = false;
		}

		if (!pre && !post && !isLegitFormatSymbol) {
			post = true;
		}

		if (post) {
			postfix += char;
		} else {
			if (isLegitFormatSymbol) {
				core += char;
				pre = false;
			} else {
				prefix += char;
			}
		}
	});

	return { prefix, core, postfix };
}

// stored formats are in US notation; swaps each segment's number part, not its [range] bounds
function switchFormatSeparators(format: string): string {
	return format
		.split('|')
		.map((segment) => {
			const numberEnd = segment.includes('[') ? segment.indexOf('[') : segment.length;
			const { prefix, core, postfix } = splitFormat(segment.slice(0, numberEnd));

			return prefix + switchDotsAndCommas(core) + postfix + segment.slice(numberEnd);
		})
		.join('|');
}

function hasUsFormatSeparators(format: string): boolean {
	return format.split('|').some((segment) => {
		const numberEnd = segment.includes('[') ? segment.indexOf('[') : segment.length;
		const { core } = splitFormat(segment.slice(0, numberEnd));

		return /,.*\./.test(core);
	});
}

// labels that are the format itself get localized; named ones (e.g. "Auto") stay as they are
function localizeFormatOptionLabels<T extends { value: string | null; label: string }>(
	options: T[],
	isEuropean: boolean,
): T[] {
	if (!isEuropean) {
		return options;
	}

	return options.map((option) =>
		option.label === option.value
			? { ...option, label: switchFormatSeparators(option.label) }
			: option,
	);
}

function getFormattedParts(
	number: number,
	format: string,
	allowExtraDigits: boolean,
): { prefix: string; formatted: string; postfix: string } {
	let hashes,
		prefix = '',
		postfix = '';

	if (format) {
		// optional unit - remove it now and pre/append it later,
		// because numeral does not format it properly without switching locales
		const parts = splitFormat(format);
		prefix = parts.prefix;
		postfix = parts.postfix;

		format = parts.core || '0';

		// check if we have format of f.ex. #.## -> transform it into #.[##]
		// because numeral library does not understand optional hashes
		hashes = /\.(#)+/.exec(format);
		if (hashes?.length) {
			let h = hashes[0].substr(1);
			if (allowExtraDigits && h.length < 3) {
				h = '000';
			}
			format = format.replace(hashes[0], '.[' + h + ']');
		} else if (allowExtraDigits) {
			// in cases where value is more important than format (e.g. y axis)
			// change format by adding extra # at the end
			if (format.includes('.')) {
				format += '[0]';
			} else {
				format += '.[000]';
			}
		}
	} else {
		format = '0a';
	}

	const formatted = numeral(number).format(format);

	return {
		prefix,
		formatted,
		postfix,
	};
}

function formatNumber(
	number: number,
	format = '',
	opts: {
		scale?: number | null;
		allowExtraDigits?: boolean;
		isHtml?: boolean;
		makeFirstSpaceUnbreakable?: boolean;
	} = {},
): string {
	let scale: number | null = 0,
		allowExtraDigits = false,
		isHtml = false;

	if (format === null) {
		format = ''; // auto
	}

	if (typeof opts.scale !== 'undefined') {
		scale = opts.scale;
	}

	if (typeof opts.allowExtraDigits !== 'undefined') {
		allowExtraDigits = opts.allowExtraDigits;
	}

	if (typeof opts.isHtml !== 'undefined') {
		isHtml = opts.isHtml;
	}

	let formatted;

	// duration gets formatted by different library
	if (isDurationFormat(format)) {
		formatted = getDurationString(number, format);

		// if (isHtml) {
		// 	// duration does not get "smaller" units
		// }
	} else if (format === 'R') {
		formatted = getRelativeTime(number);

		if (isHtml) {
			formatted = '<small>' + formatted + '</small>';
			const matchNumber = formatted.match(/\d+/g);

			if (matchNumber?.length) {
				formatted = matchNumber[0] + formatted.replace(matchNumber[0], '');
			}
		}
	} else {
		format = format.replace('◊', '%');

		let scalePostfix = '';

		if (scale === null) {
			// auto scale - check number
			let n = Math.abs(number);

			scale = 0;
			while (n / Math.pow(10, scale + 3) > 1) {
				scale += 3;
				n = n / Math.pow(10, scale);
			}
		}

		if (scale > 0) {
			number = number / Math.pow(10, scale);
			switch (scale) {
				case 3:
					scalePostfix = 'k';
					break;
				case 6:
					scalePostfix = 'M';
					break;
				case 9:
					scalePostfix = 'B';
					break;
				case 12:
					scalePostfix = 'T';
					break;
				default:
					scalePostfix = String(Math.pow(10, scale)).substr(1);
					break;
			}
		}

		if (format.includes('%')) {
			// is percentual, divide value
			number = number / 100;
		}

		const parts = getFormattedParts(number, format, allowExtraDigits);

		formatted = parts.formatted;

		if (scalePostfix) {
			let idx;
			if (formatted.includes('%')) {
				idx = formatted.indexOf('%'); // put the scale letter before the percent
			} else {
				idx = formatted.length;
			}
			formatted = formatted.substr(0, idx) + scalePostfix + formatted.substr(idx);
		}

		if (isHtml) {
			if (parts.prefix) {
				parts.prefix = '<small>' + parts.prefix + '</small>';
			}
			if (parts.postfix) {
				parts.postfix = '<small>' + parts.postfix + '</small>';
			}
		}

		formatted = parts.prefix + formatted + parts.postfix;
	}

	const makeFirstSpaceUnbreakable = isHtml || opts.makeFirstSpaceUnbreakable;
	if (makeFirstSpaceUnbreakable) {
		const tagless = isHtml
			? formatted.replace(/<small>/g, '').replace(/<\/small>/g, '')
			: formatted;
		if (tagless.indexOf(' ') < 5) {
			// convert first space close to the start to unbreakable
			formatted = formatted.replace(' ', '&nbsp;');
		}
	}

	return formatted;
}

function getDeliveryTimeLevel({
	hourOfDay,
	dayOfWeek,
	dayOfMonth,
	isWeekly,
	isMonthly,
	isQuarterly,
}: {
	hourOfDay: number | null;
	dayOfWeek: number[];
	dayOfMonth: number | null;
	isWeekly: boolean;
	isMonthly: boolean;
	isQuarterly: boolean;
}): number {
	let value = 0;
	// 1296 - is the max number that can be achieved with converted delivery times in months conversion
	// by adding this number we prevent that this number can't be smaller than the converted month number
	if (isQuarterly) {
		value = 1296;
	} else if (isMonthly && dayOfMonth) {
		// 528 - is the max number that can be achieved with converted delivery times in weeks conversion
		// by adding this number we prevent that this number can't be smaller than the converted week number
		value = 528 + dayOfMonth * 24;
	} else if (isWeekly) {
		// Convert to hour and add day hours
		// (cv + 1) bcos of sunday being 0 which causes result 0 which can make weekly sunday schedule equal a daily one
		value = dayOfWeek.reduce((pv, cv) => pv + (cv + 1), 0) * 24;
	}

	return value + (hourOfDay || 0);
}

function getAggregationLabel(aggregation: Aggregation): string {
	switch (aggregation) {
		case Aggregation.SUM:
			return 'Total';
		case Aggregation.AVG:
			return 'Average';
		case Aggregation.MIN:
			return 'Minimum';
		case Aggregation.MAX:
			return 'Maximum';
		case Aggregation.MEDIAN:
			return 'Median';
		default:
			return '';
	}
}

function useAggregationOnValues(
	aggregation: Aggregation,
	values: number[],
	otherPoints: DsDataPointOther[],
	othersCount: number,
): number | null {
	let value;
	const otherPoint = otherPoints.find((item) => item.typeEnum === aggregation);
	switch (aggregation) {
		case Aggregation.SUM:
			if (values.length) {
				value = values.reduce((sum, val) => (sum += val));
			}
			if (otherPoint) {
				value = (value || 0) + otherPoint.value;
			}
			break;
		case Aggregation.AVG: {
			const count = values.length + othersCount;
			let sum = 0;
			if (values.length) {
				sum = values.reduce((sum, val) => (sum += val));
			}
			if (otherPoint) {
				sum += otherPoint.value * othersCount;
			}
			value = count ? sum / count : null;
			break;
		}
		case Aggregation.MIN:
			value = Math.min.apply(Math.min, values);
			if (otherPoint) {
				value = Math.min(value, otherPoint.value);
			}
			break;
		case Aggregation.MAX:
			value = Math.max.apply(Math.max, values);
			if (otherPoint) {
				value = Math.max(value, otherPoint.value);
			}
			break;
		case Aggregation.MEDIAN: {
			const allValues = values.slice();
			if (otherPoint) {
				allValues.push(otherPoint.value);
			}

			if (allValues.length) {
				allValues.sort((a, b) => a - b);
				const half = Math.floor(allValues.length / 2);
				if (allValues.length % 2) {
					value = allValues[half];
				} else {
					value = (allValues[half - 1]! + allValues[half]!) / 2.0;
				}
			}
			break;
		}
	}
	return typeof value === 'number' ? value : null;
}

function getIndicatorClass(change: number, invert = false) {
	let classString = '';
	if (change < 0) {
		classString = invert ? ChangeIndicatorClass.POSITIVE : ChangeIndicatorClass.NEGATIVE;
	} else if (change > 0) {
		classString = invert ? ChangeIndicatorClass.NEGATIVE : ChangeIndicatorClass.POSITIVE;
	}
	return classString;
}

/**
 * From: https://stackoverflow.com/a/8831937
 * Returns a hash code from a string
 * @param  {String} str The string to hash.
 * @return {Number}    A 32bit integer
 * @see http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
function hashCode(str: string): number {
	let hash = 0;
	for (let i = 0, len = str.length; i < len; i++) {
		const chr = str.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0; // Convert to 32bit integer
	}
	return hash;
}

function getConversionRate(firstValue: number | null, lastValue: number | null) {
	if (lastValue !== null && firstValue) {
		const percentOverall = (lastValue / firstValue) * 100;
		return Math.round(percentOverall * 100) / 100;
	} else {
		return 0;
	}
}

function calculateChange(
	compareValue: number | null,
	mainValue: number | null,
	changeFunction = CompareFunction.PERCENTAGE,
): number | null {
	let change: number | null;
	{
		if (changeFunction === CompareFunction.RATIO) {
			// \Z\Util\Number::percent
			if (mainValue === null || !compareValue) {
				change = 0;
			} else {
				change = Math.abs(mainValue / compareValue) * 100;
			}
		} else if (changeFunction === CompareFunction.ABSOLUTE) {
			// \Z\Util\Number::absoluteChange
			change = (mainValue || 0) - (compareValue || 0);
		} else {
			// \Z\Util\Number::change
			if (!compareValue || !mainValue || compareValue === mainValue) {
				change = null;
			} else {
				change = ((mainValue - compareValue) / Math.abs(compareValue)) * 100;
			}
		}
	}
	return change;
}

function calculateAndFormatChange(
	compareValue: number | null,
	mainValue: number | null,
	changeFormat: string,
	changeFunction: CompareFunction,
): string {
	// first we get the change number
	let change = calculateChange(compareValue, mainValue, changeFunction);

	let changeSign = '';
	const skipChange = !!changeFormat && !changeFormat.includes('+');
	if (change && !skipChange) {
		if (change < 0) {
			changeSign = '▼ ';
		} else if (change > 0) {
			changeSign = '▲ ';
		}
	}

	// then, we format it
	// \Z\Util\Number::formatChange
	let changeStr = '';
	if (change !== null) {
		if (changeFormat) {
			let minDecimalPoints = 0,
				maxDecimalPoints = 0,
				prefix = '', // e.g. currency
				symbol = ''; // e.g. percent, bps or w/e

			// Match +0.0, 0.0, 0, 0%, £ #,###.##, +£ 0.00
			const matches = /^([^\d#]+)?([\d.,#]*)(.*)$/.exec(changeFormat);
			if (matches) {
				// a leading +/- controls the change arrow; whatever follows it is a literal prefix
				prefix = (matches[1] || '').replace(/^[+-]/, '');
				if (matches[3]) {
					symbol = matches[3];
				}

				const num = matches[2]?.split('.') || [];
				if (num[1]) {
					const numberOfZeros = num[1].split('0').length - 1,
						numberOfHashes = num[1].split('#').length - 1;
					minDecimalPoints = numberOfZeros;
					maxDecimalPoints = minDecimalPoints + numberOfHashes;
				}
			}

			// round to maxDecimalPoints
			const rounder = Math.pow(10, maxDecimalPoints);
			change = Math.round(change * rounder) / rounder;

			// remove sign from number. we already have changeSign string
			change = Math.abs(change);

			// if lacking, add required decimal points
			const decimalCount = (String(change).split('.')[1] || '').length;
			if (decimalCount < minDecimalPoints) {
				changeStr = change.toFixed(minDecimalPoints);
			} else {
				changeStr = String(change);
			}

			changeStr = changeSign + prefix + changeStr + symbol;
		} else {
			changeStr = changeSign + String(Math.abs(change)) + '%';
		}
	}

	return changeStr;
}

function getFormattedSyncInterval(syncInterval: number, addLy = false): string {
	const minutesInHour = 60,
		minutesInDay = 24 * minutesInHour;

	if (syncInterval < minutesInHour) {
		if (syncInterval === 1) {
			return 'minute';
		} else {
			return `${syncInterval} minutes`;
		}
	} else if (syncInterval < minutesInDay) {
		if (syncInterval === minutesInHour) {
			return 'hour' + (addLy ? 'ly' : '');
		} else {
			const quotient = Math.ceil(syncInterval / minutesInHour),
				remainder = syncInterval % minutesInHour;
			return `${remainder === 0 ? '' : '~'}${quotient} hours`;
		}
	} else {
		if (syncInterval === minutesInDay) {
			return addLy ? 'daily' : 'day';
		} else {
			const quotient = Math.ceil(syncInterval / minutesInDay),
				remainder = syncInterval % minutesInDay;
			return `${remainder === 0 ? '' : '~'}${quotient} days`;
		}
	}
}

function sumValues(values: (number | null)[]): number | null {
	let valueSum: number | null = null;
	values.forEach((value) => {
		if (valueSum === null) {
			valueSum = value;
		} else {
			valueSum += value || 0;
		}
	});
	return valueSum;
}

function roundToDecimals(value: number, maxDecimalPoints = 4) {
	const rounder = Math.pow(10, maxDecimalPoints);
	return Math.round(value * rounder) / rounder;
}

function roundGoalValue(
	value: number | null,
	isMetricPercentage = false,
	isMetricInverted = false,
) {
	// goals rules

	if (typeof value !== 'number') {
		return value;
	}

	let roundToDecimalPlaces = 0;
	if (isMetricPercentage) {
		roundToDecimalPlaces = 2;
	} else if (value < 10) {
		roundToDecimalPlaces = 1;
	}
	// Precise rounding: https://stackoverflow.com/a/18358056
	// Round down for inverted: https://stackoverflow.com/a/35827227
	const roundUp = !isMetricInverted,
		valueToRound = roundUp ? value : -value,
		result = +(
			Math.round(+(valueToRound + `e+${roundToDecimalPlaces}`)) + `e-${roundToDecimalPlaces}`
		);
	return roundUp ? result : -result;
}

function isTimestampBefore(ts1: number | null, ts2: number | null): boolean {
	if (ts1 === null || ts2 === null) {
		return false;
	}
	return ts1 < ts2;
}

function clamp(value: number | null, min: number, max: number) {
	if (typeof value !== 'number') {
		return value;
	}
	return Math.min(Math.max(value, min), max);
}

export {
	simpleFormatCharacters,
	isNumber,
	isDurationFormat,
	getRelativeTime,
	getIntervalTime,
	getFormattedTime,
	getFormattedPrice,
	getFormattedMonthlyPrice,
	getFormattedPeriod,
	getUnixTimestamp,
	formatNumber,
	getFormattedParts, // exporting only for the unit test
	switchFormatSeparators,
	hasUsFormatSeparators,
	localizeFormatOptionLabels,
	getDeliveryTimeLevel,
	getCurrentUnixTimestamp,
	getAggregationLabel,
	getCurrentFormattedTime,
	useAggregationOnValues,
	getIndicatorClass,
	hashCode,
	getConversionRate,
	calculateChange,
	calculateAndFormatChange,
	getFormattedSyncInterval,
	sumValues,
	roundToDecimals,
	roundGoalValue,
	isTimestampBefore,
	clamp,
};
